import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  VStack,
  HStack,
  Text,
  Button,
  Icon,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Badge,
  Flex,
  useColorModeValue,
  Grid,
  Card,
  CardBody,
  Image,
  useToast,
  IconButton,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  useDisclosure,
} from '@chakra-ui/react';
import {
  MapPin,
  Users,
  ShoppingCart,
  Plus,
  Minus,
  Clock,
  Info,
  List,
  History,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { tableOrderingService } from '../../services/tableOrderingService';
import { 
  TableOrderingContext, 
  TableInfo, 
  TableMenuCategory, 
  TableCartItem 
} from '../../types/tableOrdering';
import TableCart from './TableCart';
import OrderTrackingView from './OrderTrackingView';
import OrderHistoryView from './OrderHistoryView';
import { useHotelCurrencyFromTable } from '../../hooks/useHotelCurrency';
import { getActiveOrderCount, migrateFromSessionStorage } from '../../utils/orderStorage';

interface TableOrderingDashboardProps {
  context: TableOrderingContext;
  tableInfo: TableInfo;
}

/**
 * TableOrderingDashboard Component
 * Main interface for table ordering - shows menu and manages cart
 */
const TableOrderingDashboard: React.FC<TableOrderingDashboardProps> = ({
  context,
  tableInfo
}) => {
  const { t } = useTranslation();
  const [cartItems, setCartItems] = useState<TableCartItem[]>([]);
  const [activeCategory, setActiveCategory] = useState<string>('');
  const [currentView, setCurrentView] = useState<'menu' | 'tracking' | 'history'>('menu');
  const [trackingOrderNumber, setTrackingOrderNumber] = useState<string>('');
  const [trackingPhone, setTrackingPhone] = useState<string>('');
  const [activeOrderCount, setActiveOrderCount] = useState<number>(0);
  const { isOpen: isCartOpen, onOpen: onCartOpen, onClose: onCartClose } = useDisclosure();

  // Use hotel currency hook for consistent formatting
  const { formatAmount: formatHotelAmount } = useHotelCurrencyFromTable(tableInfo);
  
  const toast = useToast();
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');

  // Fetch menu for the restaurant
  const {
    data: menuCategories,
    isLoading: isLoadingMenu,
    error: menuError
  } = useQuery({
    queryKey: ['tableMenu', context.restaurantId],
    queryFn: async () => {
      const response = await tableOrderingService.getMenuForTable(context);
      return response.data;
    },
  });

  // Initialize component - check for existing orders and migrate if needed
  useEffect(() => {
    // Try to migrate from old sessionStorage format
    migrateFromSessionStorage();
    
    // Update active order count
    updateActiveOrderCount();
  }, []);

  // Set first category as active by default when menu loads
  React.useEffect(() => {
    if (menuCategories && menuCategories.length > 0 && !activeCategory) {
      setActiveCategory(menuCategories[0].id);
    }
  }, [menuCategories, activeCategory]);

  // Update active order count
  const updateActiveOrderCount = () => {
    const count = getActiveOrderCount();
    setActiveOrderCount(count);
  };

  // Calculate cart totals
  const cartTotal = cartItems.reduce((sum, item) => sum + item.total, 0);
  const cartItemCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  // Add item to cart
  const addToCart = (menuItem: any, quantity: number = 1, specialInstructions?: string) => {
    const existingItemIndex = cartItems.findIndex(
      item => item.menu_item_id === menuItem.id && item.special_instructions === specialInstructions
    );

    if (existingItemIndex >= 0) {
      // Update existing item
      const updatedItems = [...cartItems];
      updatedItems[existingItemIndex].quantity += quantity;
      updatedItems[existingItemIndex].total = updatedItems[existingItemIndex].quantity * Number(menuItem.price);
      setCartItems(updatedItems);
    } else {
      // Add new item
      const newItem: TableCartItem = {
        menu_item_id: menuItem.id,
        name: menuItem.name,
        price: Number(menuItem.price),
        quantity,
        special_instructions: specialInstructions,
        image_url: menuItem.image_url,
        total: Number(menuItem.price) * quantity
      };
      setCartItems([...cartItems, newItem]);
    }

    toast({
      title: t('tableOrdering.dashboard.addedToCart'),
      description: t('tableOrdering.dashboard.addedToCartDesc', { quantity, name: menuItem.name }),
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  // Remove item from cart
  const removeFromCart = (menuItemId: string, specialInstructions?: string) => {
    setCartItems(cartItems.filter(
      item => !(item.menu_item_id === menuItemId && item.special_instructions === specialInstructions)
    ));
  };

  // Update cart item quantity
  const updateCartQuantity = (menuItemId: string, newQuantity: number, specialInstructions?: string) => {
    if (newQuantity === 0) {
      removeFromCart(menuItemId, specialInstructions);
      return;
    }

    const updatedItems = cartItems.map(item => {
      if (item.menu_item_id === menuItemId && item.special_instructions === specialInstructions) {
        return {
          ...item,
          quantity: newQuantity,
          total: item.price * newQuantity
        };
      }
      return item;
    });
    setCartItems(updatedItems);
  };

  if (isLoadingMenu) {
    return (
      <Container maxW="4xl" py={6}>
        <VStack spacing={4} align="center" py={12}>
          <Text>{t('tableOrdering.dashboard.loadingMenu')}</Text>
        </VStack>
      </Container>
    );
  }

  if (menuError || !menuCategories) {
    return (
      <Container maxW="4xl" py={6}>
        <VStack spacing={4} align="center" py={12}>
          <Text color="red.500">{t('tableOrdering.dashboard.menuFailed')}</Text>
          <Button onClick={() => window.location.reload()}>{t('tableOrdering.dashboard.refresh')}</Button>
        </VStack>
      </Container>
    );
  }

  // Navigation handlers
  const handleBackToMenu = () => {
    setCurrentView('menu');
    setTrackingOrderNumber('');
    setTrackingPhone('');
    updateActiveOrderCount();
  };

  const handleViewOrders = () => {
    setCurrentView('history');
  };

  const handleViewOrder = (orderNumber: string, guestPhone: string) => {
    setTrackingOrderNumber(orderNumber);
    setTrackingPhone(guestPhone);
    setCurrentView('tracking');
  };

  const handleOrderSuccess = (orderData: any, guestInfo: any) => {
    setCartItems([]);
    // Update active order count after new order
    updateActiveOrderCount();
    // Navigate to order tracking
    setTrackingOrderNumber(orderData.order_number);
    setTrackingPhone(guestInfo.phone);
    setCurrentView('tracking');
  };

  // Show order history view
  if (currentView === 'history') {
    return (
      <OrderHistoryView
        onBackToMenu={handleBackToMenu}
        onViewOrder={handleViewOrder}
        tableInfo={tableInfo}
      />
    );
  }

  // Show order tracking view if we have order details
  if (currentView === 'tracking' && trackingOrderNumber && trackingPhone) {
    return (
      <OrderTrackingView
        orderNumber={trackingOrderNumber}
        guestPhone={trackingPhone}
        onBackToMenu={handleBackToMenu}
        onViewAllOrders={handleViewOrders}
      />
    );
  }

  return (
    <Box bg={bgColor} minH="100vh">
      {/* Header */}
      <Box bg={cardBg} boxShadow="sm" position="sticky" top={0} zIndex={10}>
        <Container maxW="4xl" py={4}>
          <Flex justify="space-between" align="center">
            <VStack align="start" spacing={1}>
              <Text fontSize="xl" fontWeight="bold" color="gray.800">
                {tableInfo.restaurant.name}
              </Text>
              <HStack spacing={3} fontSize="sm" color="gray.600">
                <HStack spacing={1}>
                  <Icon as={MapPin} boxSize={4} />
                  <Text>{t('tableOrdering.dashboard.tableLabel', { number: tableInfo.table_number })}</Text>
                </HStack>
                <HStack spacing={1}>
                  <Icon as={Users} boxSize={4} />
                  <Text>{t('tableOrdering.dashboard.seats', { count: tableInfo.capacity })}</Text>
                </HStack>
              </HStack>
            </VStack>
            
            <HStack spacing={2}>
              {/* My Orders Button */}
              <Button
                variant="outline"
                leftIcon={<Icon as={List} />}
                onClick={handleViewOrders}
                position="relative"
                size="md"
              >
                {t('tableOrdering.dashboard.myOrders')}
                {activeOrderCount > 0 && (
                  <Badge
                    position="absolute"
                    top="-8px"
                    right="-8px"
                    colorScheme="green"
                    borderRadius="full"
                    px={2}
                    py={1}
                    fontSize="xs"
                  >
                    {activeOrderCount}
                  </Badge>
                )}
              </Button>

              {/* Cart Button */}
              <Button
                colorScheme="blue"
                leftIcon={<Icon as={ShoppingCart} />}
                onClick={onCartOpen}
                position="relative"
              >
                {t('tableOrdering.dashboard.cart')}
                {cartItemCount > 0 && (
                  <Badge
                    position="absolute"
                    top="-8px"
                    right="-8px"
                    colorScheme="red"
                    borderRadius="full"
                    px={2}
                    py={1}
                    fontSize="xs"
                  >
                    {cartItemCount}
                  </Badge>
                )}
              </Button>
            </HStack>
          </Flex>
        </Container>
      </Box>

      <Container maxW="4xl" py={6}>
        {/* Restaurant Info */}
        {tableInfo.restaurant.description && (
          <Box mb={6} p={4} bg={cardBg} borderRadius="lg" boxShadow="sm">
            <HStack spacing={3}>
              <Icon as={Info} color="blue.500" />
              <Text fontSize="sm" color="gray.600">
                {tableInfo.restaurant.description}
              </Text>
            </HStack>
          </Box>
        )}

        {/* Menu Categories Tabs */}
        <Tabs 
          index={menuCategories?.findIndex(cat => cat.id === activeCategory) ?? 0}
          onChange={(index) => setActiveCategory(menuCategories?.[index]?.id || '')}
          variant="line"
          colorScheme="blue"
        >
          <TabList mb={6} overflowX="auto" overflowY="hidden">
            {menuCategories?.map((category) => (
              <Tab key={category.id} whiteSpace="nowrap" px={6}>
                {category.name}
              </Tab>
            ))}
          </TabList>

          <TabPanels>
            {menuCategories?.map((category) => (
              <TabPanel key={category.id} px={0}>
                {category.description && (
                  <Text mb={4} color="gray.600" fontStyle="italic">
                    {category.description}
                  </Text>
                )}
                
                <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
                  {category.items.map((item) => (
                    <Card key={item.id} bg={cardBg} variant="outline">
                      <CardBody>
                        <Flex gap={4}>
                          {item.image_url && (
                            <Image
                              src={item.image_url}
                              alt={item.name}
                              boxSize="80px"
                              objectFit="cover"
                              borderRadius="lg"
                              flexShrink={0}
                            />
                          )}
                          
                          <VStack align="start" flex="1" spacing={2}>
                            <Text fontWeight="semibold" color="gray.800">
                              {item.name}
                            </Text>
                            
                            {item.description && (
                              <Text fontSize="sm" color="gray.600" noOfLines={2}>
                                {item.description}
                              </Text>
                            )}
                            
                            <HStack spacing={2} flexWrap="wrap">
                              <Text fontWeight="bold" color="blue.600">
                                {formatHotelAmount(Number(item.price))}
                              </Text>
                              
                              {item.preparation_time_minutes && (
                                <HStack spacing={1}>
                                  <Icon as={Clock} boxSize={3} color="gray.500" />
                                  <Text fontSize="xs" color="gray.500">
                                    {t('tableOrdering.dashboard.minutesShort', { count: item.preparation_time_minutes })}
                                  </Text>
                                </HStack>
                              )}
                            </HStack>
                            
                            {item.dietary_tags && item.dietary_tags.length > 0 && (
                              <HStack spacing={1} flexWrap="wrap">
                                {item.dietary_tags.map((tag, index) => (
                                  <Badge key={index} size="sm" colorScheme="green" variant="outline">
                                    {tag}
                                  </Badge>
                                ))}
                              </HStack>
                            )}
                            
                            <HStack spacing={2} mt="auto">
                              <Button
                                size="sm"
                                colorScheme="blue"
                                variant="outline"
                                leftIcon={<Icon as={Plus} />}
                                onClick={() => addToCart(item)}
                                isDisabled={!item.is_available}
                              >
                                {item.is_available ? t('tableOrdering.dashboard.add') : t('tableOrdering.dashboard.unavailable')}
                              </Button>
                              
                              {(() => {
                                const cartItem = cartItems.find(ci => ci.menu_item_id === item.id);
                                if (cartItem && cartItem.quantity > 0) {
                                  return (
                                    <HStack spacing={2}>
                                      <IconButton
                                        size="sm"
                                        variant="outline"
                                        aria-label={t('tableOrdering.dashboard.decreaseQty')}
                                        icon={<Icon as={Minus} />}
                                        onClick={() => updateCartQuantity(item.id, cartItem.quantity - 1)}
                                      />
                                      <Text fontWeight="semibold" minW="20px" textAlign="center">
                                        {cartItem.quantity}
                                      </Text>
                                      <IconButton
                                        size="sm"
                                        variant="outline"
                                        aria-label={t('tableOrdering.dashboard.increaseQty')}
                                        icon={<Icon as={Plus} />}
                                        onClick={() => updateCartQuantity(item.id, cartItem.quantity + 1)}
                                      />
                                    </HStack>
                                  );
                                }
                                return null;
                              })()}
                            </HStack>
                          </VStack>
                        </Flex>
                      </CardBody>
                    </Card>
                  ))}
                </Grid>
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      </Container>

      {/* Cart Drawer */}
      <Drawer isOpen={isCartOpen} placement="right" onClose={onCartClose} size="md">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>
            {t('tableOrdering.dashboard.yourOrder')}
            {cartItemCount > 0 && (
              <Badge ml={2} colorScheme="blue">
                {t('tableOrdering.dashboard.items', { count: cartItemCount })}
              </Badge>
            )}
          </DrawerHeader>
          
          <DrawerBody px={0}>
            <TableCart
              cartItems={cartItems}
              tableInfo={tableInfo}
              context={context}
              onUpdateQuantity={updateCartQuantity}
              onRemoveItem={removeFromCart}
              onCartClose={onCartClose}
              onOrderSuccess={handleOrderSuccess}
            />
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Floating Cart Button (Mobile) */}
      {cartItemCount > 0 && (
        <Box
          position="fixed"
          bottom={4}
          right={4}
          display={{ base: 'block', md: 'none' }}
        >
          <Button
            colorScheme="blue"
            size="lg"
            borderRadius="full"
            leftIcon={<Icon as={ShoppingCart} />}
            onClick={onCartOpen}
            boxShadow="lg"
          >
            {t('tableOrdering.dashboard.fabSummary', { count: cartItemCount, amount: formatHotelAmount(cartTotal) })}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default TableOrderingDashboard;